import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from '../_services/authentication.service';
import { environment } from '../../environments/environment';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: { name: string; input: any; result?: string }[];
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  messages$ = new BehaviorSubject<ChatMessage[]>([]);
  loading$ = new BehaviorSubject<boolean>(false);

  constructor(private authService: AuthenticationService) {}

  private getApiUrl(): string {
    const tenantId = this.authService.tenantIdValue;
    return `${environment.apiPrefix}${tenantId}${environment.apiSuffix}/chat`;
  }

  private getPageContext(): string {
    const hash = window.location.hash || '';
    const path = hash.replace(/^#/, '');
    if (!path || path === '/' || path === '/auth/login') return '';
    const segments = path.split('/').filter(Boolean);
    const parts: string[] = [];
    parts.push(`Current page: ${path}`);
    if (segments.includes('assets') && segments.length > 1) {
      const idx = segments.indexOf('assets');
      if (segments[idx + 1]) parts.push(`Asset ID: ${segments[idx + 1]}`);
      if (segments.includes('reports')) parts.push('Viewing asset reports');
      if (segments.includes('events')) parts.push('Viewing asset events');
      if (segments.includes('issues')) parts.push('Viewing asset issues');
    } else if (segments.includes('devices') && segments.length > 1) {
      const idx = segments.indexOf('devices');
      if (segments[idx + 1]) parts.push(`Device ID: ${segments[idx + 1]}`);
    } else if (segments.includes('reports') && segments.length > 1) {
      const idx = segments.indexOf('reports');
      if (segments[idx + 1]) parts.push(`Report ID: ${segments[idx + 1]}`);
    } else if (segments.includes('spaces') && segments.length > 1) {
      const idx = segments.indexOf('spaces');
      if (segments[idx + 1]) parts.push(`Space ID: ${segments[idx + 1]}`);
    }
    return parts.join('. ');
  }

  async sendMessage(content: string) {
    const messages = this.messages$.value;
    const userMsg: ChatMessage = { role: 'user', content };
    const updated = [...messages, userMsg];
    this.messages$.next(updated);
    this.loading$.next(true);
    const assistantMsg: ChatMessage = { role: 'assistant', content: '', toolCalls: [] };
    this.messages$.next([...updated, assistantMsg]);
    const token = this.authService.currentUserValue?.token;
    if (!token) return;
    const pageContext = this.getPageContext();
    const messagesToSend = updated.map((m) => ({ role: m.role, content: m.content }));
    try {
      const response = await fetch(this.getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: messagesToSend, context: pageContext }),
      });
      if (!response.ok || !response.body) {
        assistantMsg.content = 'Error: Failed to connect to chat service.';
        this.messages$.next([...updated, assistantMsg]);
        this.loading$.next(false);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
          } else if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            if (eventType === 'delta') {
              assistantMsg.content += data.text;
            } else if (eventType === 'tool_call') {
              assistantMsg.toolCalls!.push({ name: data.name, input: data.input });
            } else if (eventType === 'tool_result') {
              const tc = assistantMsg.toolCalls!.find((t) => t.name === data.name && !t.result);
              if (tc) tc.result = data.result;
            }
            this.messages$.next([...updated, { ...assistantMsg }]);
          }
        }
      }
    } catch (err: any) {
      assistantMsg.content += '\n\nError: ' + (err.message || 'Unknown error');
      this.messages$.next([...updated, assistantMsg]);
    }
    this.loading$.next(false);
  }

  clear() {
    this.messages$.next([]);
  }
}
